import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { Truck, PackageCheck } from "lucide-react";
import { getPromoPricing } from "@/lib/promo";

const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const { t, lang } = useLang();

  const variants = product.variants || [];
  const minPrice = variants.length
    ? Math.min(...variants.map((v) => v.price))
    : product.base_price ?? 0;
  const hasMultiplePrices =
    variants.length > 1 && variants.some((v) => v.price !== variants[0].price);
  // Promo — show reduced min-price on the card if this product has an active promo variant
  const promoVariant = variants
    .map((v) => ({ v, promo: getPromoPricing(product.id, v.id) }))
    .filter((x) => x.promo)
    .sort((a, b) => a.promo.promo - b.promo.promo)[0];
  const promoMinPrice = promoVariant?.promo?.promo ?? null;
  const promoBasePrice = promoVariant?.promo?.base ?? null;
  const promoDiscountPct = promoVariant?.promo?.discountPct ?? null;
  const badge = product.badge?.[lang];
  const heroImage =
    product.images?.[0] ||
    product.common_images?.[0] ||
    variants[0]?.images?.[0] ||
    "";
  const allOutOfStock =
    variants.length > 0 && variants.every((v) => v.in_stock === false);

  const handleAdd = (e) => {
    e.preventDefault();
    if (allOutOfStock) return;
    const defaultVariant = variants.find((v) => v.in_stock !== false) || variants[0];
    if (!defaultVariant) return;
    addItem(product, defaultVariant, 1);
    toast.success(
      lang === "fr"
        ? `${product.name.fr} ajouté au panier`
        : `${product.name.en} added to cart`
    );
  };

  return (
    <article
      data-testid={`product-card-${product.slug}`}
      className="group flex flex-col flv-fade-up"
      style={{ animationDelay: `${index * 120}ms` }}
    >
      <Link
        to={`/product/${product.slug}`}
        data-testid={`product-link-${product.slug}`}
        className="block"
      >
        <div className="aspect-[4/5] overflow-hidden bg-[#ebe7df] mb-6 rounded-sm relative">
          <img
            src={heroImage}
            alt={product.name[lang]}
            loading="lazy"
            className={`w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105 ${
              allOutOfStock ? "opacity-70 saturate-75" : ""
            }`}
          />
          {allOutOfStock && (
            <div
              data-testid={`product-oos-${product.slug}`}
              className="absolute top-4 left-4 eyebrow bg-[#1A1A1A] !text-white px-3 py-1.5 rounded-full tracking-[0.18em]"
            >
              {lang === "fr" ? "Rupture de stock 🖤" : "Out of stock 🖤"}
            </div>
          )}
          {!allOutOfStock && badge && (
            <div
              data-testid={`product-badge-${product.slug}`}
              className="absolute top-4 left-4 eyebrow bg-[#1A1A1A] !text-white px-3 py-1.5 rounded-full tracking-[0.18em]"
            >
              {badge}
            </div>
          )}
          <div className="absolute top-4 right-4 eyebrow bg-[#F5F0EB]/90 px-3 py-1.5 rounded-full text-[#1A1A1A]">
            {promoMinPrice != null ? (
              <span className="inline-flex items-baseline gap-1.5">
                <span className="text-[#B45C3F] font-medium">{promoMinPrice.toFixed(0)} {t.common.currency}</span>
                <span className="opacity-50 line-through text-[10px]">{promoBasePrice.toFixed(0)}</span>
              </span>
            ) : (
              <>
                {hasMultiplePrices && (
                  <span className="opacity-70 mr-1">{t.products.from}</span>
                )}
                {minPrice.toFixed(0)} {t.common.currency}
              </>
            )}
          </div>
          {promoMinPrice != null && !allOutOfStock && (
            <div
              data-testid={`product-promo-flag-${product.slug}`}
              className="absolute bottom-4 left-4 eyebrow bg-[#B45C3F] !text-white px-3 py-1.5 rounded-full tracking-[0.16em]"
            >
              -{promoDiscountPct}%
            </div>
          )}
        </div>
      </Link>

      <div className="flex justify-between items-start gap-6">
        <div className="min-w-0">
          <Link
            to={`/product/${product.slug}`}
            className="font-serif-display text-2xl md:text-3xl leading-tight tracking-tight hover:opacity-70 transition-opacity"
          >
            {product.name[lang]}
          </Link>
          <p className="text-sm text-[#4a4a4a] mt-2 max-w-sm leading-relaxed">
            {product.short?.[lang] || product.tagline[lang]}
          </p>

          {/* Fast-shipping reassurance (Coussin flagship) */}
          {!allOutOfStock && product.fast_ship_badge?.[lang] && (
            <div
              data-testid={`product-fast-ship-${product.slug}`}
              className="mt-4 flex flex-wrap items-center gap-2"
            >
              <span className="inline-flex items-center gap-1.5 bg-[#3D2B1F] text-white px-3 py-1.5 rounded-full text-[11px] tracking-[0.14em] uppercase font-medium">
                <Truck size={12} strokeWidth={1.8} />
                {product.fast_ship_badge[lang]}
              </span>
              {product.stock_badge?.[lang] && (
                <span className="inline-flex items-center gap-1.5 bg-[#EFE7DA] text-[#3D2B1F] border border-[#dcd7ce] px-3 py-1.5 rounded-full text-[11px] tracking-[0.14em] uppercase font-medium">
                  <PackageCheck size={12} strokeWidth={1.8} />
                  {product.stock_badge[lang]}
                </span>
              )}
            </div>
          )}

          <p
            data-testid={`product-price-note-${product.slug}`}
            className="text-xs text-[#4a4a4a] mt-3"
          >
            {promoMinPrice != null ? (
              <span className="inline-flex items-baseline gap-2">
                <span className="text-[#B45C3F] font-medium">
                  {hasMultiplePrices ? `${t.products.from} ` : ""}
                  {promoMinPrice.toFixed(2)} {t.common.currency}
                </span>
                <span className="line-through text-[#8a8378]">{promoBasePrice.toFixed(2)}</span>
              </span>
            ) : (
              <span className="text-[#1A1A1A] font-medium">
                {hasMultiplePrices ? `${t.products.from} ` : ""}
                {minPrice.toFixed(2)} {t.common.currency}
              </span>
            )}
            <span className="mx-2 opacity-40">·</span>
            {product.delivery_time_badge?.[lang] || t.product.priceNote}
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <button
          type="button"
          onClick={handleAdd}
          disabled={allOutOfStock}
          data-testid={`add-to-cart-${product.slug}`}
          className={`group inline-flex items-center justify-center gap-2 px-7 py-4 text-xs tracking-[0.22em] uppercase font-medium transition-all duration-300 rounded-md ${
            allOutOfStock
              ? "bg-[#8a8378] text-white cursor-not-allowed"
              : "bg-[#3D2B1F] text-white hover:bg-[#1A1A1A] hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-12px_rgba(61,43,31,0.55)] shadow-[0_4px_14px_-6px_rgba(61,43,31,0.45)]"
          }`}
        >
          {allOutOfStock
            ? lang === "fr"
              ? "Bientôt de retour"
              : "Back soon"
            : t.products.addToCart}
        </button>
        <Link
          to={`/product/${product.slug}`}
          data-testid={`view-product-${product.slug}`}
          className="text-xs tracking-[0.18em] uppercase underline underline-offset-[6px] decoration-[#1A1A1A]/30 hover:decoration-[#1A1A1A] transition-colors py-3.5 px-4 text-center"
        >
          {allOutOfStock
            ? lang === "fr"
              ? "M'alerter du retour"
              : "Notify me"
            : t.products.viewProduct}
        </Link>
      </div>
    </article>
  );
};

export default ProductCard;
